import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const EDUCATION_OPTIONS = [
  'Illiterate',
  'Primary',
  'Higher Secondary',
  'Graduate',
  'Agri-BSc / Technical Diploma',
];

const Step1Personal = ({ formData, onChange, errors = {} }) => {
  // Calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Handle DOB change
  const handleDOBChange = (dob) => {
    onChange('date_of_birth', dob);
    const calculatedAge = calculateAge(dob);
    if (calculatedAge !== null) {
      onChange('age', calculatedAge);
    }
  };

  // Calculate max date (18 years ago) and min date (100 years ago)
  const today = new Date();
  const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate()).toISOString().split('T')[0];
  const minDate = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate()).toISOString().split('T')[0];

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="farmer_name">
              Farmer Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="farmer_name"
              data-testid="farmer-name-input"
              value={formData.farmer_name || ''}
              onChange={(e) => onChange('farmer_name', e.target.value)}
              placeholder="Enter full name"
              className={errors.farmer_name ? 'border-red-500' : ''}
            />
            {errors.farmer_name && <p className="text-sm text-red-500">{errors.farmer_name}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="date_of_birth">
              Date of Birth <span className="text-red-500">*</span>
            </Label>
            <Input
              id="date_of_birth"
              data-testid="dob-input"
              type="date"
              max={maxDate}
              min={minDate}
              value={formData.date_of_birth || ''}
              onChange={(e) => handleDOBChange(e.target.value)}
              className={errors.date_of_birth ? 'border-red-500' : ''}
            />
            {formData.date_of_birth && formData.age && (
              <p className="text-xs text-gray-600">Age: {formData.age} years</p>
            )}
            {errors.date_of_birth && <p className="text-sm text-red-500">{errors.date_of_birth}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_number">
              Contact Number <span className="text-red-500">*</span>
            </Label>
            <Input
              id="contact_number"
              data-testid="contact-number-input"
              type="tel"
              maxLength="10"
              value={formData.contact_number || ''}
              onChange={(e) => onChange('contact_number', e.target.value.replace(/\D/g, ''))}
              placeholder="10-digit mobile number"
              className={errors.contact_number ? 'border-red-500' : ''}
            />
            {errors.contact_number && <p className="text-sm text-red-500">{errors.contact_number}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="aadhaar_number">
              Aadhaar Number <span className="text-red-500">*</span>
            </Label>
            <Input
              id="aadhaar_number"
              data-testid="aadhaar-input"
              type="text"
              maxLength="12"
              value={formData.aadhaar_number || ''}
              onChange={(e) => onChange('aadhaar_number', e.target.value.replace(/\D/g, ''))}
              placeholder="12-digit Aadhaar number"
              className={errors.aadhaar_number ? 'border-red-500' : ''}
            />
            {errors.aadhaar_number && <p className="text-sm text-red-500">{errors.aadhaar_number}</p>}
            <p className="text-xs text-gray-500">Your Aadhaar number is kept confidential</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="farming_experience_years">
              Farming Experience (years) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="farming_experience_years"
              data-testid="farming-experience-input"
              type="number"
              min="0"
              value={formData.farming_experience_years || ''}
              onChange={(e) => onChange('farming_experience_years', parseInt(e.target.value) || '')}
              placeholder="Years of farming experience"
              className={errors.farming_experience_years ? 'border-red-500' : ''}
            />
            {errors.farming_experience_years && <p className="text-sm text-red-500">{errors.farming_experience_years}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="education_level">Education</Label>
            <Select
              value={formData.education_level || ''}
              onValueChange={(value) => onChange('education_level', value)}
            >
              <SelectTrigger id="education_level" data-testid="education-level-select">
                <SelectValue placeholder="Select education level" />
              </SelectTrigger>
              <SelectContent>
                {EDUCATION_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">Helps improve scoring accuracy</p>
          </div>
        </div>
      </div>

      <div className="p-4 bg-blue-50 rounded-lg">
        <p className="text-sm text-gray-700">
          <strong>Note:</strong> All information is encrypted and stored securely. Your Aadhaar number is used only for identity verification and credit assessment.
        </p>
      </div>
    </div>
  );
};

export default Step1Personal;
